import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import StructuredData from '@/components/feature/StructuredData';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

const articleImages: Record<string, string> = {
  '1': 'https://readdy.ai/api/search-image?query=Historical%20academic%20library%20with%20vintage%20books%20and%20warm%20amber%20lighting%2C%20sophisticated%20scholarly%20atmosphere%2C%20abstract%20historical%20timeline%20elements%2C%20editorial%20photography%20style%2C%20soft%20natural%20light%20through%20windows%2C%20warm%20cream%20and%20terracotta%20tones&width=1200&height=600&seq=article-hero-1&orientation=landscape',
  '2': 'https://readdy.ai/api/search-image?query=Educational%20dictionary%20and%20terminology%20concept%20art%2C%20warm%20amber%20and%20cream%20color%20palette%2C%20abstract%20letterforms%20and%20symbols%20floating%20in%20soft%20light%2C%20clean%20modern%20editorial%20design%2C%20intellectual%20atmosphere&width=1200&height=600&seq=article-hero-2&orientation=landscape',
  '3': 'https://readdy.ai/api/search-image?query=Abstract%20artistic%20representation%20of%20role%20dynamics%20and%20identity%20spectrum%2C%20warm%20rose%20and%20terracotta%20gradient%2C%20flowing%20organic%20shapes%20merging%20and%20separating%2C%20modern%20minimalist%20design%2C%20soft%20ethereal%20lighting&width=1200&height=600&seq=article-hero-3&orientation=landscape',
  '4': 'https://readdy.ai/api/search-image?query=Minimalist%20artistic%20representation%20of%20textile%20art%20and%20rope%20craft%2C%20warm%20cream%20and%20amber%20tones%2C%20abstract%20flowing%20lines%2C%20soft%20natural%20lighting%2C%20elegant%20composition%2C%20editorial%20quality&width=1200&height=600&seq=article-hero-4&orientation=landscape',
  '5': 'https://readdy.ai/api/search-image?query=Abstract%20sensory%20experience%20artwork%2C%20warm%20amber%20and%20rose%20tones%20with%20soft%20gradients%2C%20flowing%20textures%20representing%20touch%20and%20sensation%2C%20minimalist%20elegant%20composition%2C%20soft%20diffused%20lighting%2C%20editorial%20style&width=1200&height=600&seq=article-hero-5&orientation=landscape',
  '6': 'https://readdy.ai/api/search-image?query=Minimalist%20safety%20and%20ethics%20concept%20art%2C%20warm%20sage%20green%20and%20cream%20tones%2C%20abstract%20protective%20geometric%20shapes%2C%20clean%20professional%20aesthetic%2C%20soft%20natural%20lighting%2C%20editorial%20quality%20composition&width=1200&height=600&seq=article-hero-6&orientation=landscape',
  '7': 'https://readdy.ai/api/search-image?query=Abstract%20communication%20and%20signal%20concept%20art%2C%20warm%20amber%20and%20cream%20tones%2C%20minimalist%20geometric%20shapes%20representing%20connection%2C%20clean%20modern%20design%2C%20soft%20natural%20lighting%2C%20editorial%20style&width=1200&height=600&seq=article-hero-7&orientation=landscape',
  '8': 'https://readdy.ai/api/search-image?query=Minimalist%20scene%20design%20and%20planning%20concept%2C%20warm%20amber%20and%20cream%20tones%2C%20abstract%20architectural%20elements%20suggesting%20space%20and%20atmosphere%2C%20clean%20modern%20aesthetic%2C%20soft%20directional%20lighting%2C%20editorial%20composition&width=1200&height=600&seq=article-hero-8&orientation=landscape',
  '9': 'https://readdy.ai/api/search-image?query=Abstract%20psychological%20connection%20artwork%2C%20warm%20rose%20and%20terracotta%20tones%2C%20two%20organic%20forms%20interacting%20and%20intertwining%2C%20soft%20dreamy%20atmosphere%2C%20editorial%20quality%2C%20minimalist%20composition&width=1200&height=600&seq=article-hero-9&orientation=landscape',
  '10': 'https://readdy.ai/api/search-image?query=Minimalist%20beginner%20guide%20and%20journey%20concept%2C%20warm%20amber%20and%20cream%20tones%2C%20abstract%20path%20and%20milestone%20elements%2C%20clean%20modern%20aesthetic%2C%20soft%20natural%20lighting%2C%20editorial%20composition%2C%20hopeful%20atmosphere&width=1200&height=600&seq=article-hero-10&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-foreground-950 mb-2">文章未找到</h2>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const canonicalUrl =
    typeof window !== 'undefined' ? `${window.location.origin}/article/${slug}` : '';

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={canonicalUrl}
        ogType="article"
      />
      <StructuredData
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: canonicalUrl.replace(`/article/${slug}`, ''),
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: article.category,
              item: canonicalUrl.replace(`/article/${slug}`, `/category/${article.categorySlug}`),
            },
            { '@type': 'ListItem', position: 3, name: article.title, item: canonicalUrl },
          ],
        }}
      />
      <StructuredData
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          datePublished: article.date,
          author: { '@type': 'Organization', name: article.author },
          publisher: { '@type': 'Organization', name: 'SM专业指南' },
          url: canonicalUrl,
          image: articleImages[article.id],
          inLanguage: 'zh-CN',
        }}
      />

      <Navbar />
      <main className="pt-16">
        <article>
          <div className="bg-background-50 border-b border-background-200/60">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <Breadcrumb
                items={[
                  { name: '首页', path: '/' },
                  { name: article.category, path: `/category/${article.categorySlug}` },
                  { name: article.title },
                ]}
              />
              <div className="mt-4">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 font-medium">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-500">{article.date}</span>
                  <span className="text-xs text-foreground-500">{article.readTime}阅读</span>
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight">
                  {article.title}
                </h1>
                <p className="text-foreground-600 text-base md:text-lg mt-4 leading-relaxed">
                  {article.excerpt}
                </p>
                <p className="text-xs text-foreground-500 mt-4">作者：{article.author}</p>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <div className="aspect-video rounded-xl overflow-hidden mb-10">
              <img
                src={articleImages[article.id]}
                alt={article.title}
                title={`${article.title} - SM专业指南`}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-base md:prose-lg max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/60">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 border border-background-200/60"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-12 pt-8 border-t border-background-200/60">
                <h3 className="text-lg font-semibold text-foreground-950 mb-6">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.slug}`}
                      className="group block bg-background-50 rounded-lg border border-background-200/70 p-4 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                    >
                      <h4 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {related.title}
                      </h4>
                      <p className="text-foreground-600 text-xs line-clamp-2 leading-relaxed">
                        {related.excerpt}
                      </p>
                      <div className="mt-3 flex items-center gap-1 text-xs text-primary-500 font-medium">
                        阅读全文
                        <i className="ri-arrow-right-line text-xs"></i>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}